import { ComponentIds } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { KompisProductList } from '@inter-ikea-kompis/react-components';
import { SlidingDoorComponent, UnavailableProductComponent } from '@inter-ikea-storageone/range';
import { reportRemoveAllUnavailableProducts } from 'analytics/reporters';
import { useCore } from 'contexts/core';
import { getInvalidProducts } from 'ducks/core';
import { useAppDispatch } from 'ducks/hooks';
import { hideDoors, showDoors } from 'ducks/layout';
import { getMarketProducts, getUnavailableProducts } from 'ducks/products';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import { removeProducts } from 'rangeConfig/shared/utils/removeProducts';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Box from 'ui/Box';
import Button, { ButtonTypeEnum } from 'ui/Button/Button';
import Sheet from 'ui/Sheet/Sheet';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import Body from 'ui/Typography/Body';
import Title from 'ui/Typography/Title';
import getUnavailableShoppingProducts from './utils/getUnavailbleShoppingProducts';
import { useServiceSettings } from 'contexts/serviceSettings';

const MenuContent = styled(Box)`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ListWrapper = styled.div`
  height: 90%;
  overflow-y: auto;
  margin-top: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.l};
`;

const UnavailableProductsMenu = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { translations, dexfSettings } = useServiceSettings();
  const coreApi = useCore();
  const unavailableProductsInScene = useSelector(getInvalidProducts);
  const allUnavailableProducts = useSelector(getUnavailableProducts);
  const allProducts = useSelector(getMarketProducts);

  const products = getUnavailableShoppingProducts(
    unavailableProductsInScene,
    allUnavailableProducts
  );

  const { shoppingProducts } = useFetchShoppingProducts(products);

  const removeUnavailableProductsInScene = () => {
    const unavailableEntities = coreApi.entities.getByComponent(UnavailableProductComponent.id);
    removeProducts(unavailableEntities, coreApi, allProducts);
    dispatch(showDoors());
    reportRemoveAllUnavailableProducts();
  };

  const display = unavailableProductsInScene.length > 0;

  useEffect(() => {
    const doorsInScene = coreApi.entities.getByComponents([
      ComponentIds.DOOR_COMPONENT_ID,
      SlidingDoorComponent.id
    ]);
    const unavailableDoorsInScene = coreApi.entities
      .getByComponent(UnavailableProductComponent.id)
      .filter(e => doorsInScene.includes(e));

    if (display && unavailableDoorsInScene.length === 0) {
      setTimeout(() => {
        dispatch(hideDoors());
      }, 300);
    }
  }, [coreApi, dispatch, display]);

  return (
    <Sheet visible={display}>
      <MenuContent p="xl">
        <Title type={ThemeFontStyleTypeEnum.headingMedium} pb="l">
          {formatMessage({ id: 'storageone.unavailableProductsMenuTitle' })}
        </Title>
        <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
          {formatMessage({ id: 'storageone.unavailableProductsMenuSubtitle' })}
        </Body>
        <ListWrapper>
          {translations && dexfSettings && (
            <KompisProductList
              shoppingProducts={shoppingProducts}
              translations={translations.kompis}
              settings={dexfSettings}
              theme={SkapaTheme}
              disableLinks={true}
              showItemNo={true}
            />
          )}
        </ListWrapper>
        <Button
          type={ButtonTypeEnum.primary}
          text={formatMessage({ id: 'storageone.unavailableProductsMenuRemoveButton' })}
          dataTestId={display ? 'remove-unavailable-products-btn' : ''}
          onClick={removeUnavailableProductsInScene}
          width={'100%'}
        />
      </MenuContent>
    </Sheet>
  );
};

export default UnavailableProductsMenu;
